import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Pane,
  Paragraph,
  Card,
  Table,
  Badge,
  Dialog,
  Button,
  toaster,
  Text
} from "evergreen-ui";
import { getCookie } from '../../util.js';
import GroupUser from "./GroupUser.js";
import GroupInvite from "./GroupInvite.js";
import GroupUserInvite from "./GroupUserInvite.js";
import RefreshTeamData from "./RefreshTeamData.js";
import { setPubInvites } from "../../redux/pubinvites/pubinvitesSlice.js";
import { useMutation } from '@apollo/client';
import DELETE_PUBLISHER_USER_MUTATION from "../../graphql/mutations/DeletePublisherUser.js";

function CollaboratorsPane() {
  const dispatch = useDispatch();
  const pubusers = useSelector((state) => state.pubusers?.value || []);
  const pubinvites = useSelector((state) => state.pubinvites?.value || []);
  const userroles = useSelector((state) => state.userroles?.value || []);
  const currentUser = useSelector((state) => state.user.value);

  // Debug logs
  console.log("CollaboratorsPane - Redux Store - pubusers:", pubusers);
  console.log("CollaboratorsPane - Redux Store - pubinvites:", pubinvites);
  console.log("CollaboratorsPane - Redux Store - userroles:", userroles);
  console.log("CollaboratorsPane - Redux Store - state:", useSelector((state) => state));

  // State for managing user removal confirmation
  const [userToRemove, setUserToRemove] = useState(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  // State for managing invitation cancellation
  const [inviteToCancel, setInviteToCancel] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Get the current user's role to determine permissions
  const currentUserData = pubusers.find(user => parseInt(user.user.id) === parseInt(currentUser));
  const isOwner = currentUserData?.role?.name === "owner";
  const isAdmin = currentUserData?.role?.name === "admin";

  // Check if user has admin privileges (either owner or admin)
  const hasAdminPrivileges = isOwner || isAdmin;

  // Setup the delete mutation
  const [deletePublisherUser] = useMutation(DELETE_PUBLISHER_USER_MUTATION, {
    onCompleted: (data) => {
      toaster.success('User has been removed from the team');
    },
    onError: (error) => {
      toaster.danger('Failed to remove user: ' + error.message);
    }
  });

  const addNewInvitation = (email, key) => {
    const newitem = [{ id: Date.now(), invite: { email: email, key: key } }];
    dispatch(setPubInvites([...pubinvites, ...newitem]));
  };

  const handleDeletePublisherUser = (userId) => {
    const userToDelete = pubusers.find(user => user.id === userId);
    if (userToDelete) {
      setUserToRemove(userToDelete);
      setShowRemoveDialog(true);
    } else {
      toaster.danger('User not found. Please refresh the page and try again.');
    }
  };

  const confirmUserRemoval = () => {
    if (userToRemove) {
      deletePublisherUser({
        variables: {
          id: parseInt(userToRemove.id),
        }
      });
      setShowRemoveDialog(false);
      setUserToRemove(null);
    }
  };

  const handleCancelInvite = (inviteId) => {
    const inviteToDelete = pubinvites.find(invite => invite.id === inviteId);
    if (inviteToDelete) {
      setInviteToCancel(inviteToDelete);
      setShowCancelDialog(true);
    } else {
      toaster.danger('Invitation not found. Please refresh the page and try again.');
    }
  };

  const confirmCancelInvite = () => {
    if (inviteToCancel && inviteToCancel.id && inviteToCancel.invite && inviteToCancel.invite.email) {
      // Call the API to delete the invitation
      const csrftoken = getCookie('csrftoken');

      fetch(`/api/v1/delete_invitation/${inviteToCancel.id}/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken
        }
      })
      .then(res => {
        if (res.ok) {
          // Filter out the cancelled invite from the Redux store
          const updatedInvites = pubinvites.filter(invite => invite.id !== inviteToCancel.id);
          dispatch(setPubInvites(updatedInvites));

          toaster.success(`Invitation to ${inviteToCancel.invite.email} has been cancelled`);
        } else {
          return res.json().then(data => {
            throw new Error(data.message || 'Failed to cancel invitation');
          });
        }
      })
      .catch(err => {
        toaster.danger(err.message || 'Failed to cancel invitation');
      })
      .finally(() => {
        setShowCancelDialog(false);
        setInviteToCancel(null);
      });
    } else {
      setShowCancelDialog(false);
      setInviteToCancel(null);
      toaster.danger('Invalid invitation data. Please refresh the page and try again.');
    }
  };

  return (
    <Card
      border="default"
      padding={32}
      backgroundColor="white"
      elevation={1}
    >
      {/* Component to refresh team data */}
      <RefreshTeamData />
      <div style={{ display: 'none' }}>RefreshTeamData is rendered</div>

      {/* Invite Form Section - Only show for admin/owner */}
      {hasAdminPrivileges ? (
        <GroupUserInvite
          userroles={userroles}
          addNewInvitation={addNewInvitation}
        />
      ) : (
        <Card
          padding={24}
          backgroundColor="white"
          elevation={0}
          border="muted"
        >
          <Paragraph size={500} fontWeight="bold" marginBottom={16}>Team Management</Paragraph>
          <Text>You need admin privileges to invite new team members or manage existing ones.</Text>
        </Card>
      )}

      {/* Team Members Table Section */}
      <Pane marginTop={32}>
        <Paragraph size={500} marginBottom={16}>Your Team</Paragraph>

        <Table width="100%">
          <Table.Head height={40} backgroundColor="#F4F5F9">
            <Table.TextCell flexShrink={0} flexGrow={3}>Email</Table.TextCell>
            <Table.TextCell flexShrink={0} flexGrow={2}>Role</Table.TextCell>
            <Table.TextCell flexShrink={0} flexGrow={1}>Status</Table.TextCell>
            <Table.TextCell flexShrink={0} flexGrow={2}>Actions</Table.TextCell>
          </Table.Head>
          <Table.Body>
            {pubusers.map(item => (
              <GroupUser
                publisheruser={item}
                key={item.user.id}
                userroles={userroles}
                isOwner={isOwner}
                hasAdminPrivileges={hasAdminPrivileges}
                handleDeletePublisherUser={handleDeletePublisherUser}
              />
            ))}

            {pubinvites.map(item => (
              <GroupInvite
                publisherinvite={item}
                key={`invite${item.id}`}
                isOwner={isOwner}
                hasAdminPrivileges={hasAdminPrivileges}
                handleCancelInvite={handleCancelInvite}
              />
            ))}
          </Table.Body>
        </Table>
      </Pane>

      {/* User removal confirmation dialog */}
      <Dialog
        isShown={showRemoveDialog && userToRemove !== null}
        title="Remove Team Member"
        intent="danger"
        onCloseComplete={() => setShowRemoveDialog(false)}
        confirmLabel="Remove"
        onConfirm={confirmUserRemoval}
      >
        {userToRemove && userToRemove.user ? (
          <>
            Are you sure you want to remove {userToRemove.user.email} from your team?
            This action cannot be undone.
          </>
        ) : (
          <p>Loading user information...</p>
        )}
      </Dialog>

      {/* Invitation cancellation dialog */}
      <Dialog
        isShown={showCancelDialog && inviteToCancel !== null}
        title="Cancel Invitation"
        intent="danger"
        onCloseComplete={() => setShowCancelDialog(false)}
        confirmLabel="Cancel Invitation"
        onConfirm={confirmCancelInvite}
      >
        {inviteToCancel && inviteToCancel.invite ? (
          <>
            Are you sure you want to cancel the invitation sent to {inviteToCancel.invite.email}?
          </>
        ) : (
          <p>Loading invitation information...</p>
        )}
      </Dialog>
    </Card>
  );
}

export default CollaboratorsPane;
